import { PageOptions } from '@graphcommerce/framer-next-pages'
import { HygraphPagesQuery, hygraphPageContent } from '@graphcommerce/graphcms-ui'
import { ProductListDocument, ProductListQuery } from '@graphcommerce/magento-product'
import { StoreConfigDocument, redirectOrNotFound } from '@graphcommerce/magento-store'
import { GetStaticProps, LayoutTitle, MetaRobots, PageMeta } from '@graphcommerce/next-ui'
import { Box, Container, Typography } from '@mui/material'
import { GetStaticPaths } from 'next'
import { RowRenderer, LayoutNavigation, LayoutNavigationProps } from '../../components'
import { RowContentLinks } from '../../components/GraphCMS/RowContentLinks/RowContentLinks'
import { SidebarMenu } from '../../components/GraphCMS/SidebarMenu/SidebarMenu'
import { LayoutDocument } from '../../components/Layout/Layout.gql'
import { graphqlSharedClient, graphqlSsrClient } from '../../lib/graphql/graphqlSsrClient'
import { RowConfigurator } from '../../components/Configurator/RowConfigurator'

export const config = { unstable_JsPreload: false }

type Props = HygraphPagesQuery & ProductListQuery
type RouteProps = { url: string }
type GetPageStaticPaths = GetStaticPaths<RouteProps>
export type GetPageStaticProps = GetStaticProps<LayoutNavigationProps, Props, RouteProps>

function AboutDaPage(props: Props) {
  const { pages } = props
  const page = pages?.[0]

  const title = page?.title ?? ''

  const metaRobots = page?.metaRobots.toLowerCase().split('_').flat(1) as MetaRobots[]

  return (
    <Container maxWidth='lg'>
      <PageMeta
        title={page?.metaTitle ?? title ?? ''}
        metaDescription={page?.metaDescription ?? ''}
        metaRobots={metaRobots}
        canonical={`/${page?.url}`}
      />
      <LayoutTitle>
        <Typography
          variant='h2'
          component='span'
          sx={{
            fontWeight: 700,
          }}
        >
          {title}
        </Typography>
      </LayoutTitle>

      <Box sx={{ display: { xs: 'block', md: 'flex' } }}>
        <SidebarMenu pages={pages} />
        <Box sx={{ width: '100%', '& div': { padding: '0' } }}>
          {pages?.[0] && (
            <RowRenderer
              content={pages?.[0].content}
              renderer={{
                RowContentLinks: (rowProps) => {
                  // eslint-disable-next-line react/destructuring-assignment
                  if (rowProps.identity === 'folder-image-link') {
                    return (
                      <Box sx={{ display: 'grid', placeContent: 'center' }}>
                        <RowContentLinks {...rowProps} />
                      </Box>
                    )
                  }
                  return <RowContentLinks {...rowProps} />
                },
              }}
            />
          )}
        </Box>
      </Box>
    </Container>
  )
}

AboutDaPage.pageOptions = {
  Layout: LayoutNavigation,
} as PageOptions

export default AboutDaPage

// eslint-disable-next-line @typescript-eslint/require-await
export const getStaticPaths: GetPageStaticPaths = async ({ locales = [] }) => {
  if (process.env.NODE_ENV === 'development') return { paths: [], fallback: 'blocking' }

  const urls = ['home', 'no-route']

  const paths = locales.map((locale) => urls.map((url) => ({ params: { url }, locale }))).flat(1)

  return { paths, fallback: 'blocking' }
}

export const getStaticProps: GetPageStaticProps = async ({ locale, params }) => {
  const baseUrl = 'over-da'
  const url = `${baseUrl}/${params?.url}`
  const client = graphqlSharedClient(locale)
  const staticClient = graphqlSsrClient(locale)

  const conf = client.query({ query: StoreConfigDocument })
  const page = hygraphPageContent(staticClient, url, {
    rootCategory: (await conf).data.storeConfig?.root_category_uid ?? '',
  })

  const layout = staticClient.query({ query: LayoutDocument, fetchPolicy: 'cache-first' })

  if (!(await page).data.pages?.[0]) {
    return redirectOrNotFound(staticClient, conf, { ...params, url }, locale)
  }

  // todo(paales): Remove when https://github.com/Urigo/graphql-mesh/issues/1257 is resolved
  const productList = staticClient.query({
    query: ProductListDocument,
    variables: { pageSize: 8, filters: { category_uid: { eq: 'MTAy' } } },
  })

  return {
    props: {
      ...(await page).data,
      ...(await layout).data,
      ...(await productList).data,
      up: { href: '/', title: 'Home' },
      apolloState: await conf.then(() => client.cache.extract()),
    },
    revalidate: 60 * 20,
  }
}
